import { Monitor } from "lucide-react";
import React from "react";
import { NumberTicker } from "@/components/ui/number-ticker";

// Komponent podglądu
export const NumberTickerPreview = () => (
  <div className="flex items-center justify-center">
    <NumberTicker value={1000} duration={2} />
  </div>
);

const component = {
  id: "number-ticker",
  title: "Number Ticker",
  component: NumberTicker,
  description: "A component that animates counting up to a number value.",
  preview: NumberTickerPreview,
  icon: <Monitor className="h-4 w-4 mr-2" />,
  code: `import { NumberTicker } from "@/components/ui/number-ticker";

export const NumberTickerExample = () => {
  return <NumberTicker value={1000} duration={2} />;
};`,
  installation: "This component is already included in the project.",
  detailedInstallation: `
### Installation Steps

1. This component is part of the core UI library in the project.
2. If you're using this in a new project, copy the following files:
   - \`src/components/ui/number-ticker.jsx\`
   - Make sure you have \`framer-motion\` installed in your dependencies

### Dependencies

\`\`\`json
{
  "dependencies": {
    "framer-motion": "^10.12.16",
    "react": "^18.2.0",
    "react-dom": "^18.2.0"
  }
}
\`\`\`
`,
  sourceCode: `// src/components/ui/number-ticker.jsx
import { useInView, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

export function NumberTicker({
  value,
  direction = "up",
  delay = 0,
  className,
  decimalPlaces = 0,
  ...props
}) {
  const ref = useRef(null);
  const motionValue = useMotionValue(direction === "down" ? value : 0);
  const springValue = useSpring(motionValue, {
    damping: 60,
    stiffness: 100,
  });
  const isInView = useInView(ref, { once: true, margin: "0px" });

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => {
        motionValue.set(direction === "down" ? 0 : value);
      }, delay * 1000);
      return () => clearTimeout(timer);
    }
  }, [motionValue, isInView, delay, value, direction]);

  useEffect(
    () =>
      springValue.on("change", (latest) => {
        if (ref.current) {
          ref.current.textContent = Intl.NumberFormat("en-US", {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          }).format(Number(latest.toFixed(decimalPlaces)));
        }
      }),
    [springValue, decimalPlaces]
  );

  return <span ref={ref} className={cn("", className)} {...props} />;
}
`,
  props: [
    {
      name: "value",
      type: "number",
      default: "0",
      description: "The target value to animate to.",
    },
    {
      name: "duration",
      type: "number",
      default: "2",
      description: "Animation duration in seconds.",
    },
  ],
  examples: [
    {
      title: "Basic usage",
      code: `<NumberTicker value={1000} />`,
    },
    {
      title: "Custom duration",
      code: `<NumberTicker value={5000} duration={4} />`,
    },
  ],
};

export default component;
